import { forwardRef, useImperativeHandle, useRef } from 'react';
import { debug } from 'zf-web-infra';
import { Input, Space, Tree as BaseTree } from 'antd';
import { Box } from '../box';
import { useHeight } from '../../hooks';
import { Block } from '../block';
import { Spin } from '../spin';
import { useExpand, useSearch, useTitleRender, useTreeData } from './hooks';
import { TreeService } from './service';
import styles from './style.module.less';
import type { DataSource, TreeProps, TreeRef } from './types';

import type { CSSProperties, ReactElement, Ref } from 'react';
import type { DataNode } from 'antd/es/tree';

function TreeComponent<T extends DataSource>(props: TreeProps<T>, ref: Ref<TreeRef>): ReactElement {
  debug.common('Tree', props);
  const { value,
    title,
    placeholder,
    dataSource,
    expandRoot = true,
    search: showSearch = true,
    loading = false,
    loadingTips = ''
  } = props;

  const { current: service } = useRef(new TreeService());
  const defaultExpandedKeys = useExpand(service, dataSource, expandRoot, props.defaultExpandedKeys);
  const search = useSearch(service, dataSource, defaultExpandedKeys);
  const { searchValue, onSearchChange, titleRender } = useTitleRender(search);
  const height = useHeight(`.${styles.tree as string}`);
  const loadingHeight = useHeight(`.${styles.loadingBox as string}`);
  const treeData = useTreeData(service, dataSource);
  const spinHeight: CSSProperties = { height: loadingHeight };

  // 为外部组件提供检索功能
  useImperativeHandle(ref, () => ({
    search: (keyword: string): void => {
      onSearchChange(keyword);
    }
  }));

  if (loading) {
    return (<Box className={styles.loadingBox} style={spinHeight}>
      <Spin tip={loadingTips} className={styles.treeLoading} />
    </Box>
    );
  }

  return (
    <Space direction='vertical' className={styles.treeContent}>
      <Block if={title != null}>
        <Box className={styles.title}>{title}</Box>
      </Block>
      <Block if={showSearch}>
        <Block if={props.searchBox == null}>
          <Input.Search
            className={styles.treeSearch}
            value={searchValue}
            placeholder={placeholder}
            onChange={event => {
              onSearchChange(event.target.value);
              props.onSearchChange?.(event.target.value);
            }}
          />
        </Block>
        <Block if={props.searchBox != null}>
          {props.searchBox}
        </Block>
      </Block>
      <BaseTree
        className={styles.tree}
        {...props}
        selectedKeys={value}
        showLine={props.showLine ?? true}
        showIcon={props.showIcon ?? true}
        height={height}
        autoExpandParent={search.autoExpandParent}
        blockNode={props.blockNode ?? true}
        expandedKeys={search.expandedKeys}
        treeData={treeData}
        onExpand={search.onExpand}
        onSelect={(selectedKeys, info): void => {
          /**
           * 统一将 selectedKeys 转换成 string，方便使用
           */
          props.onChange?.(selectedKeys.map(key => `${key}`), info);
          // 点击节点时切换展开/收起状态
          const key = `${info.node.key}`;
          const newExpandedKeys = search.expandedKeys.includes(key)
            ? search.expandedKeys.filter(k => k !== key)
            : [...search.expandedKeys, key];
          search.onExpand(newExpandedKeys);
        }}
        titleRender={titleRender}
      />
    </Space>
  );
}

// TODO: 只有测试代码引用，不应该直接倒出，测试代码如何引用到这个类需要再推敲？
export * from './types';
export { TreeService, DataNode };
export const Tree = forwardRef<TreeRef, TreeProps<DataSource>>(TreeComponent);
